"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BlockEditor = exports.Previewer = void 0;
const model_1 = require("./model");
class ViewData {
    editorState = -1;
    layout = "N/A";
    quilt = new model_1.Quilt();
}
class RenderData {
    borderUnits;
    hasSash;
    cells;
    cellSize;
    padSize;
    blockSize;
    canvasSize;
    constructor(quilt, cellSizeFn) {
        const shape = quilt.shape;
        const blockCells = quilt.blockCells;
        this.hasSash = quilt.sash.levels !== model_1.Sashes.None;
        let borderUnits = 0;
        for (const border of quilt.borders) {
            borderUnits += border.cellWidth;
        }
        this.borderUnits = borderUnits;
        this.cells = new model_1.Rect(blockCells * shape.w + borderUnits + (this.hasSash ? shape.w - 1 : 0), blockCells * shape.h + borderUnits + (this.hasSash ? shape.h - 1 : 0));
        this.cellSize = cellSizeFn(this.cells);
        this.padSize = (this.cellSize * this.borderUnits) / 2;
        this.blockSize = this.cellSize * blockCells;
        this.canvasSize = this.cells.scale(this.cellSize);
    }
}
function clamp(n, lo, hi) {
    const top = typeof hi === "number" ? Math.min(n, hi) : n;
    return typeof lo === "number" ? Math.max(lo, top) : top;
}
function arrayEquals(a, b) {
    if (a.length !== b.length) {
        return false;
    }
    for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) {
            return false;
        }
    }
    return true;
}
function deepCopy(x) {
    return JSON.parse(JSON.stringify(x));
}
function getDPR(canvas, size) {
    return size.w ? canvas.width / size.w : 1;
}
function getRealDPR(DPR) {
    return Math.max(1, typeof DPR !== 'undefined' ? DPR : window.devicePixelRatio || 1);
}
function sizeCanvasTo(canvas, size, DPR) {
    const useDPR = getRealDPR(DPR);
    canvas.width = size.w * useDPR;
    canvas.height = size.h * useDPR;
    canvas.style.width = `${size.w}px`;
    canvas.style.height = `${size.h}px`;
}
function getDrawSize(available, maxSize, minSize, quantum, aspect) {
    aspect ??= available.w / available.h;
    const quantumW = Math.max(1, Math.floor(quantum * aspect));
    const limit = new model_1.Rect(clamp(available.w, minSize.w, maxSize.w) | 0, clamp(available.h, minSize.h, maxSize.h) | 0);
    let w = limit.w - (limit.w % quantumW), h = limit.h - (limit.h % quantum);
    const tmpAspect = w / h;
    if (tmpAspect > aspect) {
        w = h * aspect;
    }
    else if (tmpAspect < aspect) {
        h = w / aspect;
    }
    if (w < (minSize.w ?? -1)) {
        w = minSize.w;
        h = minSize.w / aspect;
    }
    if (h < (minSize.h ?? -1)) {
        w = minSize.h * aspect;
        h = minSize.h;
    }
    return new model_1.Rect(Math.round(w), Math.round(h));
}
class Previewer {
    DPR;
    maxSize;
    minSize;
    canvas;
    view;
    constructor(canvas, maxSize, minSize) {
        this.canvas = canvas;
        this.maxSize = maxSize;
        this.minSize = minSize || new model_1.RectBounds();
        this.view = new ViewData();
        this.resizeToBounds(new model_1.Rect(canvas.width, canvas.height));
    }
    _drawSize;
    get drawSize() {
        return this._drawSize;
    }
    resizeToBounds(available, aspect, quantum = 1) {
        this._drawSize = getDrawSize(available, this.maxSize, this.minSize, quantum, aspect);
    }
    render(quilt, sizeFn, seq) {
        const r = new RenderData(quilt, sizeFn), v = this.view, cellSize = r.cellSize, visQuilt = v.quilt, sash = quilt.sash;
        const layout = `${cellSize},${r.cells},${r.hasSash ? "sash" : "noSash"}`;
        let fullRedraw = layout !== v.layout || v.editorState < 0 || typeof seq !== "number";
        if (fullRedraw) {
            v.layout = layout;
            sizeCanvasTo(this.canvas, r.canvasSize, this.DPR);
            v.quilt = new model_1.Quilt(quilt.shape);
        }
        else if (!v.quilt.colorSet.equals(quilt.colorSet) ||
            (r.hasSash && !arrayEquals(v.quilt.sash.colors, quilt.sash.colors))) {
            fullRedraw = true;
        }
        const ctx = this.canvas.getContext("2d", { alpha: false });
        ctx.save();
        const DPR = this.DPR || getDPR(this.canvas, r.canvasSize);
        ctx.scale(DPR, DPR);
        this.drawBorders(fullRedraw ? undefined : visQuilt.borders, quilt.borders, ctx, r);
        v.quilt.borders = deepCopy(quilt.borders);
        if (r.hasSash) {
            this.drawSash(fullRedraw ? null : visQuilt.sash, sash, quilt.shape, ctx, r);
            v.quilt.sash = deepCopy(sash);
        }
        else if (visQuilt.sash.levels !== model_1.Sashes.None) {
            v.quilt.sash = new model_1.SashInfo();
        }
        if (fullRedraw || seq !== v.editorState) {
            this.drawBlocks(quilt, ctx, r);
            v.editorState = seq;
            v.quilt.colorSet = quilt.colorSet.copy();
        }
        ctx.restore();
    }
    drawBorders(prior, borders, ctx, r) {
        let oX = 0;
        let oY = 0;
        let w = r.canvasSize.w;
        let h = r.canvasSize.h;
        let drawRest = false;
        for (let i = 0; i < borders.length; i++) {
            const border = borders[i];
            const viewBorder = prior && i < prior.length ? prior[i] : null;
            if (border.cellWidth === 0 && (viewBorder?.cellWidth || 0) === 0) {
                continue;
            }
            const delta = border.cellWidth * r.cellSize;
            const strip = delta / 2;
            if (drawRest || !border.equals(viewBorder)) {
                ctx.beginPath();
                ctx.rect(oX, oY, w, h);
                ctx.rect(oX + strip, oY + strip, w - delta, h - delta);
                ctx.closePath();
                ctx.fillStyle = border.color;
                ctx.fill("evenodd");
                if (!drawRest && border.cellWidth !== viewBorder?.cellWidth) {
                    drawRest = true;
                }
            }
            oX += strip;
            oY += strip;
            w -= delta;
            h -= delta;
        }
    }
    drawSash(prior, sash, shape, ctx, r) {
        if (sash.levels === model_1.Sashes.None) {
            return;
        }
        const sashSpacing = r.cellSize;
        const blockSize = r.blockSize;
        const padSize = r.padSize;
        const borderSize = 2 * padSize;
        const viewColors = prior && prior.levels === sash.levels ? prior.colors : [];
        const drawMain = !(prior && viewColors && viewColors[0] === sash.colors[0]);
        const stepSize = blockSize + sashSpacing;
        const padStepSize = padSize + stepSize;
        if (drawMain) {
            ctx.fillStyle = sash.colors[0];
            for (let col = 1, oX = padStepSize; col < shape.w; col++) {
                ctx.fillRect(oX - sashSpacing, padSize, sashSpacing, r.canvasSize.h - borderSize);
                oX += stepSize;
            }
            for (let row = 1, oY = padStepSize; row < shape.h; row++) {
                ctx.fillRect(padSize, oY - sashSpacing, r.canvasSize.w - borderSize, sashSpacing);
                oY += stepSize;
            }
        }
        if (sash.levels !== model_1.Sashes.Double) {
            return;
        }
        else if (!drawMain && viewColors.length >= 2 && viewColors[1] === sash.colors[1]) {
            return;
        }
        ctx.fillStyle = sash.colors[1];
        for (let col = 1, oX = padStepSize; col < shape.w; col++) {
            for (let row = 1, oY = padStepSize; row < shape.h; row++) {
                ctx.fillRect(oX - sashSpacing, oY - sashSpacing, sashSpacing, sashSpacing);
                oY += stepSize;
            }
            oX += stepSize;
        }
    }
    drawBlocks(q, ctx, r) {
        const blockSize = r.blockSize;
        const padSize = r.padSize;
        const sashSize = r.hasSash ? r.cellSize : 0;
        const deviceSize = r.blockSize * getRealDPR(this.DPR);
        const sourceCount = q.blocks.length;
        const scaled = new Array(sourceCount);
        for (let i = 0; i < sourceCount; i++) {
            scaled[i] = q.blocks[i].getScaledSource(deviceSize);
        }
        const stepSize = blockSize + sashSize;
        let oY = padSize;
        let oX = padSize;
        for (const rowMap of q) {
            for (const iBlock of rowMap) {
                const src = scaled[iBlock];
                ctx.drawImage(src, 0, 0, deviceSize, deviceSize, oX, oY, blockSize, blockSize);
                oX += stepSize;
            }
            oY += stepSize;
            oX = padSize;
        }
    }
}
exports.Previewer = Previewer;
class BlockEditor {
    _current = 0;
    lastCellPx = -1;
    lastGuides;
    lastColors;
    maxSize;
    minSize;
    _canvas;
    constructor(canvas, maxWidth, minWidth) {
        this._canvas = canvas;
        this._width = canvas.width;
        this.maxSize = new model_1.Rect(maxWidth, maxWidth);
        this.minSize = new model_1.Rect(minWidth, minWidth);
        this.lastGuides = new model_1.Guide("");
        this.lastColors = new model_1.Palette();
    }
    _state = 0;
    get state() {
        return this._state;
    }
    _width = 0;
    get width() {
        return this._width;
    }
    get currentBlock() {
        return this._current;
    }
    get canvas() {
        return this._canvas;
    }
    resizeToBounds(width, height) {
        const draw = getDrawSize(new model_1.Rect(width, height), this.maxSize, this.minSize, 12);
        this._width = Math.min(draw.w, draw.h, this.maxSize.w);
    }
    render(quilt, guides) {
        const colors = quilt.colorSet, block = quilt.blocks[this._current], editor = this._canvas;
        const cellCount = block.getSize();
        let dirty = block.isDirty();
        const cW = 2 * Math.floor(this._width / cellCount / 2);
        const pixelSize = cW * cellCount;
        if (cW !== this.lastCellPx || editor.style.width === "") {
            this.lastCellPx = cW;
            sizeCanvasTo(editor, new model_1.Rect(pixelSize, pixelSize));
            dirty = true;
        }
        else if (!guides.equals(this.lastGuides) || !colors.equals(this.lastColors)) {
            dirty = true;
        }
        if (!dirty) {
            return;
        }
        ++this._state;
        const ctx = editor.getContext("2d", { alpha: false });
        ctx.drawImage(block.getSource(editor.width, colors), 0, 0);
        this.drawGuides(guides, block, ctx);
        this.lastGuides = guides.copy();
        this.lastColors = colors.copy();
    }
    drawGuides(guides, block, ctx) {
        if (guides.type === model_1.GuideType.None) {
            return;
        }
        const cW = this.lastCellPx;
        const cellCount = block.getSize();
        const pixelSize = cW * cellCount;
        ctx.save();
        try {
            let at = 0;
            ctx.strokeStyle = guides.color;
            ctx.beginPath();
            for (let i = 1; i < cellCount; ++i) {
                at += cW;
                ctx.moveTo(at, 0);
                ctx.lineTo(at, pixelSize);
                ctx.moveTo(0, at);
                ctx.lineTo(pixelSize, at);
            }
            ctx.stroke();
        }
        catch (e) {
            console.error(e);
        }
        ctx.restore();
    }
}
exports.BlockEditor = BlockEditor;
